import { useState, useEffect } from 'react';
import { CrtSection, CrtField, CrtNotice } from './crtKit';

const fmtAgo = (iso, nowMs) => {
  if (!iso) return null;
  const s = Math.max(0, Math.round((nowMs - new Date(iso).getTime()) / 1000));
  return s < 60 ? `${s}s ago` : `${Math.round(s / 60)}m ago`;
};

/**
 * FANUC DGNOS page — adapter connection health + sim↔adapter drift. The drift
 * check (execution / part count) is the reason the sim and adapter views both
 * exist; this page makes "is the wire truthful right now" the headline.
 */
const fmt = (v) => (v == null || v === '' ? '—' : String(v));

export default function DiagnosticsScreen({ adapter }) {
  const {
    focas, values, reachable, err, live, source, engine, running,
    drift, execDrift, partDrift, simExec, adExec, simParts, adParts,
  } = adapter;

  // SIM ↔ ADAPTER reconciliation rows — only those with a value on either side.
  const simProg = running?.program;
  const adProg = engine?.program;
  const rows = [
    { label: 'Execution', sim: simExec, ad: adExec, drift: execDrift },
    {
      label: 'Part count', sim: simParts, ad: adParts, drift: partDrift,
      delta: Number.isFinite(simParts) && Number.isFinite(adParts) ? Math.abs(simParts - adParts) : null,
    },
    { label: 'Program', sim: simProg, ad: adProg, drift: simProg && adProg && simProg !== adProg },
  ].filter((r) => r.sim != null || r.ad != null);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const status = !reachable ? 'UNREACHABLE' : live ? 'LIVE' : 'IDLE';
  const pollAgo = focas?.lastPollAt ? fmtAgo(focas.lastPollAt, now) : null;

  return (
    <div className="text-sm space-y-3">
      {!reachable && (
        <CrtNotice tone="warn">
          focas-hero not reachable{err ? ` (${err})` : ''}. Expected in local dev without the adapter; on prod
          the adapter serves this machine.
        </CrtNotice>
      )}

      {reachable && rows.length > 0 && (
        <CrtSection title={drift ? '⚠ Sim ↔ adapter — drift' : 'Sim ↔ adapter'}>
          <div className="fanuc-cell overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="fanuc-label text-[10px] uppercase tracking-wide">
                  <th className="text-left px-3 py-2" />
                  <th className="text-right px-3 py-2">Sim</th>
                  <th className="text-right px-3 py-2">Adapter</th>
                  <th className="text-right px-3 py-2 w-10">Δ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label} style={{ borderTop: '1px solid color-mix(in srgb, var(--fx-dim) 25%, transparent)' }}>
                    <td className="px-3 py-1.5 fanuc-label">{r.label}</td>
                    <td className="px-3 py-1.5 text-right font-mono fanuc-value tabular-nums">{fmt(r.sim)}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                      <span className="fanuc-value" style={r.drift ? { color: '#fca5a5', textShadow: '0 0 6px rgba(239,68,68,0.5)' } : undefined}>
                        {fmt(r.ad)}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums"
                      style={{ color: r.drift ? '#fca5a5' : 'var(--fx-dim)' }}>
                      {r.drift ? (r.delta != null ? r.delta : '⚠') : '·'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CrtSection>
      )}

      <CrtSection title="Connection">
        <div className="fanuc-cell p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          <CrtField label="Status" value={status} danger={!reachable} />
          <CrtField label="Source" value={source} />
          {focas?.port != null && <CrtField label="Port" value={focas.port} />}
          {focas?.bound != null && <CrtField label="Bound" value={focas.bound ? 'yes' : 'no'} />}
          {pollAgo && reachable && <CrtField label="Last poll" value={pollAgo} />}
        </div>
      </CrtSection>

      {reachable && values && (
        <CrtSection title="Polled">
          <div className="fanuc-cell p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            <CrtField label="Macros" value={Object.keys(values.macros || {}).length} />
            <CrtField label="Params" value={Object.keys(values.params || {}).length} />
          </div>
        </CrtSection>
      )}
    </div>
  );
}
